import { provideAnimations } from '@angular/platform-browser/animations';
import { TUI_SANITIZER, TuiRootModule } from '@taiga-ui/core';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
} from '@angular/core';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
} from '@angular/router';

import { provideStore } from '@ngxs/store';
import { StorageOption, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import {
  TUI_ENGLISH_LANGUAGE,
  TUI_ITALIAN_LANGUAGE,
  TUI_LANGUAGE,
  TuiCountryIsoCode,
} from '@taiga-ui/i18n';
import {
  HttpClientModule,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { provideServiceWorker } from '@angular/service-worker';
import { WINDOW } from '@shared/providers/window.provider';
import { NAVIGATOR } from '@shared/providers/navigator.provider';
import { APP_ENVIRONMENT } from '@shared/providers/environment.provider';
import { NgDompurifySanitizer } from '@tinkoff/ng-dompurify';
import * as Sentry from '@sentry/angular';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { TranslocoLoaderService } from '@shared/services/transloco-loader.service';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { map } from 'rxjs';
import { routes } from './app.routes';
import { AuthState } from './store/auth/auth.state';
import { INDEXED_DB_CONFIG } from './config/indexed-db';
import { httpInterceptor } from './http.interceptor';
import { environment } from '../environments/environment';

function appInitializerFactory(
  sentry: Sentry.TraceService,
  transloco: TranslocoService,
) {
  return () => {
    const lang = transloco.getActiveLang() ?? TuiCountryIsoCode.GB;
    return transloco.load(lang);
  };
}

function languageFactory(transloco: TranslocoService) {
  return transloco.langChanges$.pipe(
    map(lang => {
      if (lang === TuiCountryIsoCode.IT) {
        return TUI_ITALIAN_LANGUAGE;
      }
      return TUI_ENGLISH_LANGUAGE;
    }),
  );
}
export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_ENVIRONMENT,
      useFactory: () => environment,
    },
    { provide: WINDOW, useValue: window },
    { provide: NAVIGATOR, useValue: navigator },
    {
      provide: TUI_LANGUAGE,
      useFactory: languageFactory,
      deps: [TranslocoService],
    },
    {
      provide: TUI_SANITIZER,
      useClass: NgDompurifySanitizer,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [Sentry.TraceService, TranslocoService],
      multi: true,
    },
    provideAnimations(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([httpInterceptor])),
    provideOAuthClient(),
    provideStore(
      [AuthState],
      withNgxsStoragePlugin({
        keys: ['auth', 'catalogs', 'kanban'],
        storage: StorageOption.LocalStorage,
      }),
      withNgxsReduxDevtoolsPlugin({
        disabled: false,
      }),
    ),
    provideTransloco({
      config: {
        availableLangs: [TuiCountryIsoCode.GB, TuiCountryIsoCode.IT],
        defaultLang: localStorage.getItem('lang') || TuiCountryIsoCode.GB,
        reRenderOnLangChange: true,
        prodMode: false,
      },
      loader: TranslocoLoaderService,
    }),
    importProvidersFrom(
      HttpClientModule,
      TuiRootModule,
      NgxIndexedDBModule.forRoot(INDEXED_DB_CONFIG),
    ),
    provideServiceWorker('my-service-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
